import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { get } from '../network/api';

import { encodeId } from '../lib/url';

import Layout from '../layout/layout';
import CheckoutSection from './ComponentsPage/CheckoutSection';
import CheckoutContent from './ComponentsPage/CheckoutContent';
import PoweredByFooter from './ComponentsPage/PoweredByFooter';
import SectionSummaryOrder from './ComponentsPage/SectionSummaryOrder';
import { useBusinessSettings } from './hooks/useBusinessSettings';
import { usePaymentStatus } from './hooks/usePaymentStatus';

type ApiError = {
  detail: string;
  status_code: number;
};

const buildCanceledFallbackData = (invoiceId?: string) =>
  ({
    customer: {
      name: '',
      email: '',
      phone: '',
      address: '',
    },
    invoice_number: invoiceId || '-',
    invoice_date: '',
    po_number: '-',
    status: 'CANCELLED',
    company_information: '',
    billing_information: '',
    currency: 'IDR',
    payment_link_expiration: '',
    invoice_payment_items: [],
    notes: '',
    bank_account_detail: '',
    tax: 0,
    discount: 0,
    fees: 0,
    subtotal: 0,
    total: 0,
    reference_id: '',
    redirect_url: '',
    business: {
      name: '',
      address: '',
      country: '',
      country_code: '',
      phone_number: '',
      email: '',
      website: '',
      logo_path: '',
      primary_color: '#ffffff',
      header_title: '',
      button_color: '#000000',
      button_text_color: '#ffffff',
      secondary_color: '#f3f4f6',
      sidebar_text_color: '#111827',
      sidebar_text_secondary_color: '#6b7280',
      theme_name: '',
      default: '',
    },
    merchant: {
      name: '-',
      merchant_ref: '',
      website: '',
    },
    payment: {
      status: 'FAILED',
      string: '',
    },
  }) as const;

const CheckoutPages: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { id } = query as { id?: string };

  const getDetailCheckout = async (id: string) => {
    const encodedId = encodeId(id);
    return get(`/checkout/v1/detail?invoice_number=${encodedId}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, isLoading, error, refetch } = useQuery<any, ApiError>({
    queryKey: ['getDetailCheckout', id],
    queryFn: async () => {
      if (!id) throw new Error('ID is required');
      return getDetailCheckout(id);
    },
    enabled: !!id,
    retry: false,
  });

  const isInvoiceCanceledError =
    error?.status_code === 400 &&
    String(error?.detail || '')
      .toLowerCase()
      .includes('invoice canceled');

  const resolvedData =
    data?.data ||
    (isInvoiceCanceledError ? buildCanceledFallbackData(id) : undefined);

  useEffect(() => {
    if ((error && !isInvoiceCanceledError) || !id) {
      navigate('/error', {
        state: {
          message: error ? error.detail : 'An unknown error occurred',
        },
      });
    }
  }, [error, navigate, id, isInvoiceCanceledError]);

  useEffect(() => {
    if (!resolvedData) return;
    if (resolvedData.status === 'PAID') return;
    const expiration = resolvedData.payment_link_expiration;
    if (!expiration) return;

    const expirationMs = new Date(expiration).getTime();

    const check = () => {
      if (Date.now() >= expirationMs) {
        navigate('/error', {
          state: {
            message:
              'The payment link has expired. Please contact the merchant to obtain a new payment link.',
          },
        });
      }
    };

    check();
    const interval = setInterval(check, 2000);
    return () => clearInterval(interval);
  }, [resolvedData, navigate]);

  const businessSettings = useBusinessSettings(resolvedData);
  const isPaymentActive = usePaymentStatus(resolvedData);

  const summarySlot = resolvedData ? (
    <div
      className="relative col-span-4 hidden h-full md:mr-[calc(-50vw+50%)] md:block md:pr-[calc(50vw-50%)]"
      style={{
        backgroundColor: businessSettings.primary_color || '#EBF2FF',
      }}
    >
      <div className="h-full overflow-y-auto lg:py-8 lg:pl-12 lg:pr-4">
        <SectionSummaryOrder data={resolvedData} settings={businessSettings} />
      </div>
    </div>
  ) : null;

  return (
    <Layout
      data={resolvedData}
      summarySlot={summarySlot}
      isLoading={isLoading}
      settings={businessSettings}
    >
      <CheckoutSection isPaymentActive={isPaymentActive}>
        <CheckoutContent
          data={resolvedData}
          onRefreshData={refetch}
          settings={businessSettings}
        />
        <div className="block px-3 pb-32 md:hidden">
          <SectionSummaryOrder
            data={resolvedData}
            settings={businessSettings}
          />
        </div>
        <PoweredByFooter />
      </CheckoutSection>
    </Layout>
  );
};

export default CheckoutPages;
